/**
 * SALE-026 — commissionnement : règles, bulletins, périodes, simulation.
 *
 * 🔴 **Tout `Decimal` Pydantic arrive en JSON sous forme de CHAÎNE — et ce
 * module n'envoie presque que des `Decimal`.** Assiettes, taux, bornes de
 * paliers, plafonds, objectifs : une trentaine de champs.
 *
 * Le défaut n'est pas théorique, il est déjà en production ailleurs : un
 * `raw.discount_max_pct as number` est un cast qui MENT, `"0.00" === 0` vaut
 * `false` (l'avertissement ne s'affiche jamais) et `25 !== "25.00"` fait partir
 * un PATCH à chaque enregistrement. Sur un bulletin, le piège devient
 * arithmétique : `"396.00" + "960.00"` donne `"396.00960.00"`, et un tri de
 * paliers en chaînes place **`"1000" < "50000" < "9000"`** — la grille s'affiche
 * dans le désordre et le contrôle de continuité conclut à un trou inexistant.
 *
 * **Prescription** : type honnête (`number | string | null`, jamais `number`
 * seul, jamais `any`), coercion **une fois** dans le `select` du hook — jamais
 * au rendu, sinon chaque composant refait le travail et l'un d'eux l'oubliera.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { API_BASE, apiFetch } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import { queryKeys } from '@/lib/queryKeys'

const ENDPOINT = `${API_BASE}/sales`

/** Un décimal du serveur : chaîne le plus souvent, nombre parfois, absent aussi. */
export type Money = number | string | null

export type BlockedByModule = 'BILLING' | 'SESSIONS'

export interface Availability<T extends string> {
    code: T
    available: boolean
    blocked_by: BlockedByModule | null
    requires_margin_pricing?: boolean
}

export interface CommissionTier {
    id?: string
    from_amount: Money
    to_amount: Money
    rate_pct: Money
}

export interface CommissionRule {
    id: string
    entity_id: string
    name: string
    version: number
    root_rule_id: string | null
    valid_from: string
    valid_to: string | null
    priority: number
    applies_to_descendants: boolean
    base_kind: string
    revenue_recognition_event: string
    period_kind: string
    currency: string
    tier_mode: 'MARGINAL' | 'TOTAL'
    revenue_rate_pct: Money
    margin_rate_pct: Money
    deal_flat_amount: Money
    gate_pct: Money
    accelerator_pct: Money
    accelerator_scope: 'MARGINAL' | 'TOTAL' | null
    cap_amount: Money
    cap_scope: 'PERIOD' | 'YEAR' | null
    cap_overflow_carry: boolean
    max_pct_of_margin: Money
    flat_bonus_amount: Money
    flat_bonus_threshold_pct: Money
    minimum_guarantee_amount: Money
    minimum_guarantee_months: number | null
    /** `false` = minimum de variable versé même sur une période sans vente.
     *  `?? true` à la lecture : le champ a été ajouté au contrat le 2026-08-13. */
    minimum_guarantee_requires_sale?: boolean
    include_expense_lines: boolean
    include_article_lines: boolean
    include_sources: string[]
    discount_malus_threshold_pct: Money
    discount_malus_rate_pct: Money
    scope_user_ids: string[] | null
    campaign_ids: string[] | null
    catalog_price_bonus_pts: Money
    client_type_new_factor: Money
    client_type_existing_factor: Money
    client_type_inbound_factor: Money
    funding_opco_factor: Money
    /** @deprecated Migration 485 — un coefficient par campagne. Encore LU pour
     *  les barèmes enregistrés avant, jamais réécrit. */
    campaign_factor: Money
    /** SALE-026 §5bis.C — coefficient multiplicatif PAR campagne. */
    campaign_factors: Record<string, Money> | null
    catalog_rates: Record<string, Money> | null
    payment_terms_threshold_days: number | null
    payment_terms_factor: Money
    team_pool_pct: Money
    override_rate_pct: Money
    ytd_true_up: boolean
    payment_delay_months: number
    b2c_holdback_days: number
    clawback_window_months: number
    clawback_on_unpaid: boolean
    clawback_unpaid_days: number | null
    clawback_on_credit_note: boolean
    clawback_on_cancelled_session: boolean
    is_cumulative: boolean
    is_override: boolean
    archived_at: string | null
    created_at: string
    tiers: CommissionTier[]
    /**
     * Combien de commissions ce barème a DÉJÀ produites.
     *
     * ⚠️ C'est la frontière entre un BROUILLON et un document opposable, et elle
     * est FACTUELLE : à zéro, l'écran propose « Enregistrer » et « Supprimer » ;
     * au premier euro calculé, R14 reprend la main et la seule option devient
     * « Créer une nouvelle version ». Le client ne redérive rien.
     */
    line_count: number
}

export interface CommissionRulesResponse {
    rules: CommissionRule[]
    available_revenue_events: Availability<string>[]
    available_base_kinds: Availability<string>[]
    templates: { code: string; payload: Record<string, unknown> }[]
    /** La devise d'un barème CRÉÉ ici — celle que `POST /commission-rules` lui donnera. */
    default_currency?: string
}

export interface CommissionLineItem {
    id: string
    label: string
    line_type: string
    quantity: Money
    net_amount: Money
    currency: string
    margin_amount: Money
    status: 'IN_BASE' | 'EXCLUDED'
    excluded_reason: string | null
    /** Absents du payload sans `catalog:view_floor_price` — retirés, jamais vidés. */
    floor_unit_amount?: Money
    floor_currency?: string | null
    catalog_base_amount?: Money
    reference_source?: 'FLOOR' | 'PREAPPROVED' | null
}

export interface CommissionLine {
    id: string
    quote_id: string | null
    quote_auto_reference: string
    client_display_name: string | null
    beneficiary_user_id: string
    beneficiary_display_name: string
    campaign_display_name: string | null
    event_kind: string
    event_at: string
    original_event_at: string | null
    event_local_month: string
    event_local_tz: string
    currency: string
    revenue_base: Money
    margin_base: Money
    excluded_amount: Money
    excluded_line_count: number
    rate_applied: Money
    share_pct: Money
    amount: Money
    payable_from: string | null
    rule_id: string
    rule_version: number
    /** 🔴 Qui a VENDU — sur une commission d'équipe, le bénéficiaire est le
     *  responsable, jamais le vendeur. */
    seller_display_name?: string | null
    tier_breakdown: Record<string, unknown>[] | null
    target_amount_snapshot: Money
    status: 'ACQUIRED' | 'ADJUSTMENT' | 'WAIVED'
    is_clawback: boolean
    items: CommissionLineItem[]
    /** L'examen du manager (§13.24). ⚠️ `PENDING` **se verse** : l'acte de
     *  gestion est le REJET, jamais l'absence de validation. */
    review_status: CommissionReviewStatus
    reviewed_by_name: string | null
    reviewed_at: string | null
    /** 🔴 Rendu au commercial sur SON bulletin : personne ne doit découvrir un
     *  rejet sur sa fiche de paie. */
    review_comment: string | null
}

export type CommissionReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED'

/**
 * L'atteinte d'UN barème.
 *
 * ⚠️ Elle n'appartient jamais à une personne : celle-ci peut porter un socle au
 * chiffre d'affaires ET une prime de conquête dont l'objectif se compte en
 * clients. Un pourcentage unique pour les deux mesurerait des euros contre un
 * nombre de clients.
 */
export interface RuleAttainment {
    rule_id: string
    /** Les versions que cette carte regroupe : une carte par BARÈME (arbitrage A28). */
    version_ids?: string[]
    rule_name: string | null
    /** L'objectif est un NOMBRE de clients — l'écran écrit « 8 / 10 », pas « 8 € ». */
    counts_clients: boolean
    /** 🔴 Ce sont les affaires de son ÉQUIPE, pas les siennes. Sans ce drapeau, le bulletin d'un
     *  responsable mêle ses ventes et son encadrement, où les mêmes colonnes disent deux choses. */
    is_override: boolean
    target: Money
    realised: Money
    attainment_pct: Money
    amount: Money
    /** Les trois formes de rémunération — trois barèmes paient trois choses : un % du CA, un %
     *  de la marge, ou un FORFAIT par affaire. Sans le forfait, « 50 € par nouveau client »
     *  s'affichait « 4 500 € … 50 € » : sans lien, et le premier n'est même pas l'assiette. */
    rate_pct?: Money
    margin_rate_pct?: Money
    deal_flat_amount?: Money
    /** Ces trois taux pour CHAQUE version de `version_ids` : une ligne s'explique par ceux de SA version. */
    version_settings?: Record<string, Pick<RuleAttainment, 'rate_pct' | 'margin_rate_pct' | 'deal_flat_amount'>>
    /** Le SEUIL de déclenchement, en % de l'objectif. `null` = aucune condition.
     *  La jauge le matérialise par un trait : pas de trait, pas de condition. */
    gate_pct?: Money
    /** 🔴 L'atteinte DÉCIDE-t-elle quelque chose ? Sur un taux plat sans seuil,
     *  non : le taux s'applique dès le premier euro. L'afficher à côté du
     *  montant se lit pourtant comme sa condition. */
    attainment_matters?: boolean

    /**
     * 🔥 **La grille de paliers, et où l'on en est dessus.** « Ça motive toute une équipe à
     * atteindre le seuil pour que tout le monde ait la prime » (PO, 2026-08-14) — encore faut-il
     * que le seuil se VOIE.
     *
     * ⚠️ Renseignés uniquement quand la plage couvre **une seule période** : un palier se franchit
     * dans un mois et repart à zéro au suivant. Sur un trimestre, le serveur se tait plutôt que
     * d'additionner trois assiettes contre un seuil mensuel.
     */
    tiers?: { from_amount: string; to_amount: string | null; rate_pct: string }[]
    /** `TOTAL` = le taux atteint s'applique à TOUT. La phrase en dépend
     *  entièrement : « la totalité passe à 1 % » n'est vraie qu'en `TOTAL`. */
    tier_mode?: 'MARGINAL' | 'TOTAL' | null
    /** L'assiette qui décide du palier — la MARGE sur un barème à la marge. */
    tier_base?: Money
    current_tier_rate_pct?: Money
    next_tier_from?: Money
    next_tier_rate_pct?: Money
    /** Ce qu'il reste à vendre pour franchir. Absent = dernier palier atteint. */
    to_next_tier?: Money
}

export interface BeneficiaryBlock {
    beneficiary_user_id: string
    beneficiary_display_name: string
    entity_id: string
    entity_name: string | null
    currency: string
    lines: CommissionLine[]
    total: Money
    /** Le détail par barème. Une seule entrée : l'en-tête suffit. */
    rules?: RuleAttainment[]
    /** ⚠️ `null` dès que la personne porte PLUSIEURS barèmes — un pourcentage
     *  unique désignerait alors l'un des objectifs sans dire lequel. */
    target: Money
    attainment_pct: Money
    excluded_summary: { count: number; amount: Money; by_reason: Record<string, number> }
    /** Ce que le manager a rejeté — HORS de `total`, qui est ce qui sera versé. */
    rejected_total?: Money
    rejected_count?: number
    /** Ce qu'il reste à examiner. Payable en l'état : c'est un reste à faire pour
     *  le manager, pas une retenue sur la paie de quelqu'un. */
    pending_count?: number
}

export interface CommissionPeriod {
    id: string
    entity_id: string
    period_kind: string
    period_start: string
    period_end: string
    currency: string
    status: 'OPEN' | 'CLOSED' | 'PAID'
    closed_at: string | null
    paid_at: string | null
    line_count: number
    total_amount: Money
    beneficiary_count: number
}

export interface CommissionStatement {
    /** La période UNIQUE — renseignée seulement quand la plage n'en couvre qu'une. */
    period: CommissionPeriod | null
    /** Toutes les périodes couvertes par la plage, la plus récente d'abord. */
    periods: CommissionPeriod[]
    blocks: BeneficiaryBlock[]
    grand_total: Money
    currency: string | null
    can_view_all: boolean
    /** Assiettes paramétrées mais non calculables : l'écran doit LE DIRE. */
    unavailable_bases: string[]
    service_start: string | null
}

export interface CommissionPreviewPart {
    rule_id: string
    rule_name: string
    amount: Money
    /** Cette part vient du barème d'ÉQUIPE : elle rémunère l'affaire d'un
     *  subordonné, pas une vente personnelle. */
    is_override: boolean
}

export interface CommissionQuotePreview {
    is_estimate: boolean
    currency: string | null
    /** 🔴 La SOMME de tous ses barèmes — socle, primes cumulables et barème
     *  d'équipe. Le bandeau n'en montrait qu'un, et annonçait donc moins que
     *  ce que le bulletin paiera. */
    amount: Money
    /** Le détail par barème : un montant composite sans décomposition se lit
     *  comme un chiffre tombé du ciel. */
    parts: CommissionPreviewPart[]
    revenue_base: Money
    margin_base: Money
    excluded_summary: { count: number; amount: Money }
    rule_id: string | null
    rule_name: string | null
    rate_applied: Money
    no_rule_reason: string | null
    split_share_pct: Money
}

export interface CommissionSplit {
    id?: string
    user_id: string
    share_pct: Money
    display_name?: string | null
    /** Gelé au fait générateur : après, le partage ne se rediscute plus. */
    locked_at?: string | null
}

export interface CommissionSplitsResponse {
    splits: CommissionSplit[]
    locked: boolean
}

export interface CommissionAlert {
    code: string
    severity: string
    quote_id: string | null
    quote_auto_reference: string | null
    beneficiary_user_id: string | null
    beneficiary_display_name: string | null
    amount: Money
    currency: string | null
    detail: Record<string, unknown>
}

function headers(entityId: string | null | undefined): HeadersInit {
    const h: Record<string, string> = { 'Content-Type': 'application/json' }
    // ⚠ `apiFetch` n'ajoute PAS `x-entity-id` : chaque appel le passe
    // explicitement, faute de quoi le serveur rend un 400 silencieux.
    if (entityId) h['X-Entity-Id'] = entityId
    return h
}

export function useCommissionRules(entityId: string | null | undefined, enabled = true) {
    return useQuery<CommissionRulesResponse>({
        queryKey: queryKeys.commissions.rules(entityId ?? ''),
        queryFn: async () => {
            const response = await apiFetch(`${ENDPOINT}/commission-rules`, {
                headers: headers(entityId),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!entityId && enabled,
        staleTime: 60_000,
    })
}

/**
 * §13.35 — une jauge du bandeau « où j'en suis ».
 *
 * ⚠️ `attainment_pct` **n'est pas plafonné** : 131,4 arrive tel quel, et c'est
 * `attainmentSegments()` qui décide comment le dessiner. Un plafonnement côté
 * serveur aurait rendu le dépassement invisible partout à la fois.
 *
 * ⚠️ `target` peut être `null` — mais alors la jauge n'est pas servie du tout :
 * R27, un objectif absent n'est pas un objectif de zéro.
 *
 * 🔴 `prorated` doit être DIT à l'écran : un objectif de 2 151 € là où la fiche
 * du commercial annonce 4 167 € est incompréhensible sans le mot qui l'explique.
 */
export interface ObjectiveGauge {
    kind: 'PERIOD_REVENUE' | 'YEAR_REVENUE' | 'YEAR_CLIENTS'
    target: Money
    realised: Money
    attainment_pct: Money
    /** L'objectif mensuel a été recalculé au prorata des jours de la plage. */
    prorated: boolean
}

/**
 * Les formes de période que l'écran sait NOMMER. `CUSTOM` affiche ses deux
 * dates : « objectif recalculé » dirait qu'un calcul a eu lieu sans dire sur quoi.
 */
export type ObjectivePeriodKind = 'MONTH' | 'QUARTER' | 'HALF' | 'YEAR' | 'CUSTOM'

export interface ObjectivesResponse {
    user_id: string
    display_name: string | null
    currency: string | null
    period_start: string
    period_end: string
    period_kind: ObjectivePeriodKind
    /** L'exercice de référence : celui où la plage se TERMINE. */
    fiscal_year: number
    gauges: ObjectiveGauge[]
    /** R31 — objectif et CA dans deux devises : on renonce au pourcentage. */
    currency_mismatch: boolean
    /**
     * 🔴 **POURQUOI il n'y a aucune jauge** — `null` quand il y en a.
     *
     * Les deux raisons appellent deux phrases, et les confondre en fabrique une
     * fausse : `NO_OBJECTIVE` dit « personne ne vous en a fixé », `NOT_ON_PERIOD`
     * dit « vous en avez, mais pas sur la plage affichée » (R27). Servir la
     * première à quelqu'un du second cas l'enverrait réclamer ce qu'il a déjà.
     */
    empty_reason: 'NO_OBJECTIVE' | 'NOT_ON_PERIOD' | null
}

/** La plage affichée. Absente, le serveur retombe sur le mois courant. */
export interface ObjectiveRange {
    from?: string | null
    to?: string | null
}

function rangeQuery(range?: ObjectiveRange): string {
    const params = new URLSearchParams()
    if (range?.from) params.set('from', range.from)
    if (range?.to) params.set('to', range.to)
    const query = params.toString()
    return query ? `?${query}` : ''
}

/**
 * §13.35 — ma progression sur MES objectifs, sur la période affichée.
 *
 * Servi à côté du bulletin, jamais à sa place : le bulletin dit ce qui sera
 * VERSÉ, ces jauges disent où l'on en est de ce qui a été FIXÉ.
 *
 * ⚠️ La plage est DANS la clé de cache : sans elle, changer de mois afficherait
 * la progression du mois précédent jusqu'au prochain montage.
 */
export function useCommissionObjectives(
    entityId: string | null | undefined,
    range?: ObjectiveRange,
    enabled = true,
) {
    return useQuery<ObjectivesResponse>({
        queryKey: [
            ...queryKeys.commissions.objectives(entityId ?? ''),
            range?.from ?? '',
            range?.to ?? '',
        ],
        queryFn: async () => {
            const response = await apiFetch(
                `${ENDPOINT}/commissions/objectives${rangeQuery(range)}`,
                { headers: headers(entityId) },
            )
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!entityId && enabled,
        staleTime: 120_000,
    })
}

export interface TeamObjectiveMember {
    id: string
    name: string
}

/**
 * §13.35 — la progression de l'ÉQUIPE, pour l'onglet du responsable.
 *
 * 🔑 Gardé par `sales_profile:read`, le droit qui garde déjà cette donnée dans
 * les statistiques — pas par un droit de commission. Un objectif n'est pas une
 * rémunération.
 *
 * ⚠️ `members` porte TOUJOURS l'équipe entière, même quand `rows` est filtré sur
 * une personne : sans cela, le sélecteur qui a servi à filtrer se viderait de
 * ses autres options au premier choix.
 */
export function useTeamObjectives(
    entityId: string | null | undefined,
    options?: { range?: ObjectiveRange; userId?: string | null; enabled?: boolean },
) {
    const { range, userId, enabled = true } = options ?? {}
    return useQuery<{ rows: ObjectivesResponse[]; members: TeamObjectiveMember[] }>({
        queryKey: [
            ...queryKeys.commissions.teamObjectives(entityId ?? ''),
            range?.from ?? '',
            range?.to ?? '',
            userId ?? '',
        ],
        queryFn: async () => {
            const query = rangeQuery(range)
            const url = userId
                ? `${ENDPOINT}/commissions/objectives/team${query || '?'}${query ? '&' : ''}user_id=${encodeURIComponent(userId)}`
                : `${ENDPOINT}/commissions/objectives/team${query}`
            const response = await apiFetch(url, { headers: headers(entityId) })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!entityId && enabled,
        staleTime: 120_000,
    })
}

/**
 * Cette entrée de menu mène-t-elle quelque part ? — servi à la NAVIGATION.
 *
 * L'entrée « Commissions et objectifs » ne doit s'afficher que si un barème
 * **ou** un objectif concerne réellement la personne : un menu qui propose un
 * écran toujours vide est du bruit (arbitrage PO du 2026-08-13). C'est le
 * pendant exact des « Demandes envoyées », qui n'apparaissent qu'une fois la
 * première demande émise.
 *
 * ⚠ `has_rule` a gardé son nom quand la seconde condition est arrivée
 * (2026-08-14) : le client le lit comme « cette entrée mène quelque part », et
 * le renommer aurait imposé un déploiement synchrone des deux côtés.
 *
 * ⚠ Appelé par le MENU, donc sur chaque page : le serveur en fait deux lectures
 * d'index, et on garde la réponse longtemps en cache.
 */
export function useCommissionSelfStatus(entityId: string | null | undefined, enabled = true) {
    return useQuery<{ has_rule: boolean }>({
        queryKey: [...queryKeys.commissions.all, 'self-status', entityId ?? ''],
        queryFn: async () => {
            const response = await apiFetch(`${ENDPOINT}/commissions/self-status`, {
                headers: headers(entityId),
            })
            if (!response.ok) return { has_rule: false }
            return response.json()
        },
        enabled: !!entityId && enabled,
        staleTime: 300_000,
    })
}

export function useCommissionPeriods(entityId: string | null | undefined, enabled = true) {
    return useQuery<CommissionPeriod[]>({
        queryKey: queryKeys.commissions.periods(entityId ?? ''),
        queryFn: async () => {
            const response = await apiFetch(`${ENDPOINT}/commissions/periods`, {
                headers: headers(entityId),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!entityId && enabled,
        staleTime: 60_000,
    })
}

export function useCommissionStatement(
    entityId: string | null | undefined,
    params: {
        periodId?: string | null
        userId?: string | null
        /** Plage de dates du sélecteur du design system (SALE-021). */
        from?: string | null
        to?: string | null
        /**
         * `'self'` — MON bulletin, et rien d'autre. Le serveur refuse alors
         * d'élargir au périmètre de lecture, même à un super-admin : « la table
         * des commissions, c'est uniquement pour soi » (PO, 2026-08-13).
         */
        scope?: 'self' | 'team'
    } = {},
    enabled = true,
) {
    const { periodId = null, userId = null, from = null, to = null, scope = 'self' } = params
    return useQuery<CommissionStatement>({
        queryKey: queryKeys.commissions.statement(
            from && to ? `${from}:${to}` : (periodId ?? 'current'),
            // Le périmètre entre dans la CLÉ : sans lui, « mon bulletin » et
            // « celui de l'équipe » partageraient le même cache dès que le
            // sélecteur de commercial est sur « Toute l'équipe » — et l'un
            // servirait les chiffres de l'autre.
            `${scope}:${userId ?? ''}`,
        ),
        queryFn: async () => {
            const query = new URLSearchParams()
            // La PLAGE prime : c'est elle que pilote le sélecteur du design
            // system, et le serveur retient toutes les périodes qu'elle couvre.
            if (from && to) {
                query.set('from', from)
                query.set('to', to)
            } else if (periodId) {
                query.set('period_id', periodId)
            }
            if (userId) query.set('user_id', userId)
            // Le serveur porte le même verrou : l'URL se tape à la main, et un
            // `user_id` omis ne doit pas ouvrir l'entière entité.
            query.set('scope', scope)
            const response = await apiFetch(
                `${ENDPOINT}/commissions/statement${query.toString() ? `?${query}` : ''}`,
                { headers: headers(entityId) },
            )
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!entityId && enabled,
        staleTime: 30_000,
    })
}

/** « 2 400 € commissionnables sur ce devis — estimation à ce jour. » */
export function useCommissionPreview(
    quoteId: string | null,
    entityId: string | null | undefined,
    enabled = true,
    /** Marqueur de révision du devis (`updated_at`, ou le total à défaut) : il
     *  entre dans la clé pour qu'une modification de ligne, de remise ou de
     *  grille recalcule le montant immédiatement. */
    revision?: string,
) {
    return useQuery<CommissionQuotePreview>({
        queryKey: queryKeys.commissions.quotePreview(quoteId ?? '', revision),
        queryFn: async () => {
            const response = await apiFetch(`${ENDPOINT}/quotes/${quoteId}/commission-preview`, {
                headers: headers(entityId),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!quoteId && !!entityId && enabled,
        // ⚠️ **Ce montant dépend du CUMUL DE PÉRIODE, pas seulement de ce devis.**
        //
        // Il bouge sans que ce devis change : le commercial gagne une autre
        // affaire et franchit un seuil, son équipe atteint son objectif, un
        // barème prend effet. Un cache servi à la réouverture de la page
        // afficherait alors un montant que le bulletin contredira.
        //
        // `refetchOnWindowFocus` est désactivé globalement (`main.tsx`) : sans
        // `refetchOnMount: 'always'`, revenir sur le devis dans les 15 secondes
        // — précisément le geste de quelqu'un qui vient de gagner une affaire et
        // revient vérifier — servait la valeur périmée. Une requête légère à
        // chaque ouverture est le prix d'un chiffre d'argent juste.
        refetchOnMount: 'always',
        staleTime: 15_000,
    })
}

export function useCommissionAlerts(entityId: string | null | undefined, enabled = true) {
    return useQuery<CommissionAlert[]>({
        queryKey: queryKeys.commissions.alerts(entityId ?? ''),
        queryFn: async () => {
            const response = await apiFetch(`${ENDPOINT}/commissions/alerts`, {
                headers: headers(entityId),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!entityId && enabled,
        staleTime: 120_000,
    })
}

/**
 * L'ÉQUIPE d'un responsable, et l'objectif que son barème d'équipe lira.
 *
 * L'équipe ne se saisit nulle part : elle se lit sur la hiérarchie déjà en place
 * (`users.manager_id` / `director_id`), celle qui fait déjà remonter les
 * dérogations de remise. Un second endroit où déclarer une équipe divergerait du
 * premier au premier changement d'organigramme.
 */
export interface TeamMember {
    id: string
    name: string
    /** `null` = il manque au moins un mois à cette personne (R27). */
    annual_target: Money
}

export interface TeamPreview {
    members: TeamMember[]
    /**
     * Le responsable DE cette personne — pour dire le SENS de la hiérarchie.
     *
     * 🔴 Une relation a deux bouts, et l'écran n'en montrait qu'un : « personne
     * ne dépend de cette personne » s'affichait à quelqu'un qui venait de
     * renseigner un responsable sur cette même fiche, dans l'autre sens.
     */
    manager: TeamMember | null
    /** Qui pourrait être rattaché — pour composer l'équipe sans quitter l'écran. */
    candidates: TeamMember[]
    /** Somme des objectifs des membres, ou `null` si l'un d'eux est incomplet. */
    annual_target: Money
    currency: string
    incomplete: TeamMember[]
}

/**
 * Composer l'équipe depuis l'écran de barème.
 *
 * ⚠️ **Écrit `users.manager_id`, qui ne sert pas qu'aux commissions** : la même
 * colonne porte l'escalade des dérogations de remise et les relances. L'écran le
 * dit avant d'enregistrer — « la notion de hiérarchie peut servir pour d'autres
 * choses » (PO, 2026-08-13).
 */
export function useSaveCommissionTeam(
    userId: string | null,
    entityId: string | null | undefined,
) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (memberIds: string[]): Promise<TeamPreview> => {
            const response = await apiFetch(`${ENDPOINT}/commission-rules/team/${userId}`, {
                method: 'PUT',
                headers: { ...headers(entityId), 'Content-Type': 'application/json' },
                body: JSON.stringify({ member_ids: memberIds }),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: queryKeys.commissions.all }),
    })
}

export function useCommissionTeam(
    userId: string | null,
    entityId: string | null | undefined,
    enabled = true,
) {
    return useQuery<TeamPreview>({
        queryKey: [...queryKeys.commissions.all, 'team', entityId ?? '', userId ?? ''],
        queryFn: async () => {
            const response = await apiFetch(`${ENDPOINT}/commission-rules/team/${userId}`, {
                headers: headers(entityId),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!entityId && !!userId && enabled,
        staleTime: 60_000,
    })
}

/**
 * Les personnes qui peuvent RECEVOIR une commission — celles qui ont un barème.
 *
 * ⚠️ C'est le bon ensemble pour un partage d'affaire, et pas l'annuaire : donner
 * 40 % à quelqu'un sans barème ne produit rien du tout, en silence.
 */
export function useCommissionBeneficiaries(
    entityId: string | null | undefined,
    enabled = true,
    /**
     * `'facts'` — les gens dont je consolide les commissions (bulletin).
     * `'config'` — les gens dont un barème s'applique DANS cette entité, en
     * comptant ceux hérités d'une entité mère (partage d'affaire).
     *
     * ⚠️ Ce n'est pas un détail : la configuration DESCEND. Sur le périmètre des
     * faits, un barème posé sur l'entité mère ne remontait pas, et le sélecteur
     * de partage restait vide sur toutes les filiales.
     */
    scope: 'facts' | 'config' = 'facts',
) {
    return useQuery<{ id: string; name: string; email: string | null }[]>({
        queryKey: [...queryKeys.commissions.all, 'beneficiaries', entityId ?? '', scope],
        queryFn: async () => {
            const url = `${ENDPOINT}/commissions/beneficiaries?scope=${scope}`
            const response = await apiFetch(url, { headers: headers(entityId) })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!entityId && enabled,
        staleTime: 120_000,
    })
}

/**
 * Le partage d'une affaire entre plusieurs commerciaux.
 *
 * ⚠️ **Ce n'est PAS un champ, c'est une table** : `quotes.owner_id` est
 * scalaire. Un grand compte se gagne à deux — A chasse, B tient la technique —
 * et le modèle le prévoit depuis l'origine (§5bis.D `SPLIT_MULTI_OWNER`). Seul
 * l'écran manquait : la route existait, le moteur divisait déjà, et personne ne
 * pouvait rien saisir.
 */
export function useQuoteCommissionSplits(
    quoteId: string | null,
    entityId: string | null | undefined,
    enabled = true,
) {
    return useQuery<CommissionSplitsResponse>({
        queryKey: queryKeys.commissions.splits(quoteId ?? ''),
        queryFn: async () => {
            const response = await apiFetch(`${ENDPOINT}/quotes/${quoteId}/commission-splits`, {
                headers: headers(entityId),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!quoteId && !!entityId && enabled,
        staleTime: 30_000,
    })
}

/** Upsert ATOMIQUE de la répartition complète — le serveur exige Σ = 100. */
export function useSaveQuoteCommissionSplits(
    quoteId: string,
    entityId: string | null | undefined,
) {
    const queryClient = useQueryClient()
    return useMutation<CommissionSplitsResponse, Error, { user_id: string; share_pct: string }[]>({
        mutationFn: async (splits) => {
            const response = await apiFetch(`${ENDPOINT}/quotes/${quoteId}/commission-splits`, {
                method: 'PUT',
                headers: headers(entityId),
                body: JSON.stringify(splits),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.commissions.all })
        },
    })
}

export function useSaveCommissionRule(entityId: string | null | undefined) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload: Record<string, unknown>) => {
            const response = await apiFetch(`${ENDPOINT}/commission-rules`, {
                method: 'POST',
                headers: headers(entityId),
                body: JSON.stringify(payload),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.commissions.rules(entityId ?? '') })
            // Une nouvelle version de règle change le « 2 400 € commissionnables »
            // affiché sous le bloc totaux d'un devis DÉJÀ à l'écran : toutes les
            // simulations changent, on invalide en bloc.
            void queryClient.invalidateQueries({ queryKey: queryKeys.commissions.all })
        },
    })
}

/** Réécrit un BROUILLON — le serveur refuse en 409 dès qu'il a servi. */
export function useUpdateDraftRule(entityId: string | null | undefined) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ ruleId, payload }: { ruleId: string; payload: Record<string, unknown> }) => {
            const response = await apiFetch(`${ENDPOINT}/commission-rules/${ruleId}`, {
                method: 'PUT',
                headers: headers(entityId),
                body: JSON.stringify(payload),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.commissions.all })
        },
    })
}

/** Ce que le RATTRAPAGE de mise en service produirait sur la période en cours. */
export interface CommissionBackfill {
    /** ⚠ La question se pose-t-elle ? **Décidé par le serveur.** Une période
     *  close répond zéro affaire elle aussi, mais pour une raison qui mérite une
     *  phrase — le client ne peut pas déduire ce drapeau d'un compte nul. */
    offer: boolean
    period_start: string
    period_end: string
    deal_count: number
    beneficiary_count: number
    total_amount: string
    currency: string
    /** `PERIOD_CLOSED` (la paie est partie) ou `NOT_A_SETUP` (nouvelle version :
     *  ces affaires sont déjà payées par la version remplacée). */
    blocked_by: string | null
}

/**
 * L'aperçu est **mesuré**, pas estimé : le serveur écrit réellement les lignes
 * dans une transaction annulée. Il promet donc exactement ce que le rattrapage
 * produira — ce qu'une simulation affaire par affaire ne saurait pas faire sur
 * une formule incrémentale.
 */
export function useCommissionBackfillPreview(
    ruleId: string | null,
    entityId: string | null | undefined,
) {
    return useQuery<CommissionBackfill>({
        queryKey: [...queryKeys.commissions.all, 'backfill', ruleId, entityId],
        enabled: Boolean(ruleId) && Boolean(entityId),
        // Un aperçu périmé annoncerait un rattrapage qui n'a plus lieu d'être.
        staleTime: 0,
        queryFn: async () => {
            const response = await apiFetch(
                `${ENDPOINT}/commission-rules/${ruleId}/backfill-preview`,
                { headers: headers(entityId) },
            )
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
    })
}

export function useRunCommissionBackfill(entityId: string | null | undefined) {
    const queryClient = useQueryClient()
    return useMutation<CommissionBackfill, Error, string>({
        mutationFn: async (ruleId: string) => {
            const response = await apiFetch(`${ENDPOINT}/commission-rules/${ruleId}/backfill`, {
                method: 'POST',
                headers: headers(entityId),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        onSuccess: () => {
            // Le rattrapage écrit des lignes ET recule la prise d'effet du
            // barème : bulletins, barèmes et simulations changent d'un coup.
            void queryClient.invalidateQueries({ queryKey: queryKeys.commissions.all })
        },
    })
}

export function useArchiveCommissionRule(entityId: string | null | undefined) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (ruleId: string) => {
            const response = await apiFetch(`${ENDPOINT}/commission-rules/${ruleId}`, {
                method: 'DELETE',
                headers: headers(entityId),
            })
            if (!response.ok) await handleApiError(response)
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.commissions.all })
        },
    })
}

export function useClosePeriod(entityId: string | null | undefined) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ periodId, action }: { periodId: string; action: 'close' | 'pay' }) => {
            const response = await apiFetch(
                `${ENDPOINT}/commissions/periods/${periodId}/${action}`,
                { method: 'POST', headers: headers(entityId) },
            )
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        // Réussi OU refusé : un 409 (période versée dans un autre onglet) montre l'état réel.
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.commissions.all })
            // La clôture verrouille l'écriture des objectifs (409 côté serveur) :
            // la jauge du tableau de bord doit cesser de proposer une saisie que le serveur refusera.
            void queryClient.invalidateQueries({
                queryKey: queryKeys.salesStats.targets(entityId ?? ''),
            })
            void queryClient.invalidateQueries({ queryKey: queryKeys.salesStats.all })
        },
    })
}

/**
 * Le manager valide ou rejette un LOT de lignes (§13.24).
 *
 * ⚠️ Le motif n'est pas une option au rejet — le serveur ET la base le refusent
 * vide. C'est ce texte que le commercial lira sur son propre bulletin, et sans
 * lui son montant baisse sans que personne ne sache dire pourquoi.
 */
export function useReviewCommissionLines(entityId: string | null | undefined) {
    const queryClient = useQueryClient()
    return useMutation<
        { updated: number; review_status: CommissionReviewStatus },
        Error,
        { periodId: string; lineIds: string[]; action: CommissionReviewStatus; comment?: string }
    >({
        mutationFn: async ({ periodId, lineIds, action, comment }) => {
            const response = await apiFetch(
                `${ENDPOINT}/commissions/periods/${periodId}/review`,
                {
                    method: 'POST',
                    headers: headers(entityId),
                    body: JSON.stringify({
                        line_ids: lineIds,
                        action,
                        comment: comment ?? null,
                    }),
                },
            )
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.commissions.all })
        },
    })
}

export interface SimulationInput {
    user_id: string
    amount: string
    margin: string
    is_new_client: boolean
    is_inbound: boolean
    discount_pct: string
    attainment_pct: string
    /** Le corps du barème EN COURS D'ÉDITION : fourni, lui seul est chiffré, rien n'est écrit. */
    draft?: Record<string, unknown>
}

export interface SimulationResult {
    currency: string
    parts: { rule_id: string; rule_name: string; amount: Money; inactive_reason: string | null }[]
    total: Money
    margin_left: Money
    pct_of_revenue: Money
    pct_of_margin: Money
    warn_above_pct: Money
}

/**
 * « Sur une affaire type, cette personne toucherait combien ? » ⚠️ Calculé par le
 * SERVEUR, avec les fonctions de l'acquisition : le refaire ici donnerait deux
 * implémentations du même barème (R33). Avec `draft`, c'est le brouillon qui est
 * chiffré (décision PO du 2026-09-25).
 */
export function useSimulateDeal(entityId: string | null | undefined) {
    return useMutation<SimulationResult, Error, SimulationInput>({
        mutationFn: async (body) => {
            const response = await apiFetch(`${ENDPOINT}/commissions/simulate-deal`, {
                method: 'POST',
                headers: headers(entityId),
                body: JSON.stringify(body),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
    })
}
